/**
 * What a customer's reply means (ADR 0158).
 *
 * A reply used to sit in somebody's Gmail and the app behaved as if the
 * customer had never answered. `gmail-sync` now fetches replies on a schedule
 * (ADR 0157); this module is the second half: which of a short, fixed list of
 * meanings a reply has, so the to-do list can say "Tu Le said go ahead"
 * instead of nothing.
 *
 * Jev (TypeSafe's `jev-latest`) does the reading. It picks one meaning from
 * the list below and says how sure it is. It never writes a word anybody reads
 * as fact: the task's wording is ours, chosen here by meaning and confidence,
 * and the only customer text on the list is the customer's own, quoted.
 *
 * Pure, like the rest of core. `supabase/functions/read-inbound/request.ts`
 * transcribes `replyText`, `replyReadingRequest` and `readReplyReading`,
 * because an Edge Function cannot import the workspace, and
 * `supabase/test/read-inbound-request.test.ts` holds the two copies together.
 */

import { stripQuotedReply } from "./emailThread.js";

/** TypeSafe's model, pinned by name so a reading row says what read it. */
export const REPLY_READING_MODEL = "jev-latest";

/**
 * The meanings, in the order Jev is told to prefer them when a reply does more
 * than one thing. A leak reported in the same breath as a thank-you is a leak.
 */
export const REPLY_MEANINGS = [
  "problem",
  "wants_call",
  "accepted",
  "paid",
  "question",
  "no_action",
] as const;

export type ReplyMeaning = (typeof REPLY_MEANINGS)[number];

/**
 * What a reading row holds when Jev's answer was not one of the meanings.
 * Stored rather than skipped, so the same message is not asked about again on
 * every run; the to-do list treats it as "reply owed", which is the truth.
 */
export const REPLY_UNCLEAR = "unclear";

export type StoredReplyMeaning = ReplyMeaning | typeof REPLY_UNCLEAR;

/** What each meaning is, as Jev is told it. */
export const REPLY_MEANING_CRITERIA: Record<ReplyMeaning, string> = {
  problem:
    "Reports a problem or something urgent: a leak, damage, a mistake, something missing, or unhappiness with the work, the crew or the price.",
  wants_call:
    "Asks to be called or visited, or to talk, meet or set a time to speak.",
  accepted:
    "Agrees to go ahead: accepts the quote, approves the work or a change, or says to proceed or to schedule it.",
  paid: "Says they have paid or are paying: a check mailed or dropped off, a payment made, or a payment on its way.",
  question:
    "Asks a question, or asks for something that needs a written answer, such as a document, a change to the quote, or information.",
  no_action:
    "Needs nothing from the company: thanks, an acknowledgement, a confirmation of something already agreed, an automatic out-of-office reply, or a newsletter.",
};

/** Messages per request. Small: each carries up to `REPLY_TEXT_MAX` of text. */
export const REPLY_READING_BATCH = 20;

/** The most of a reply Jev reads. A reply longer than this is a letter. */
export const REPLY_TEXT_MAX = 2000;

/** The most of our own last message Jev reads, for what "yes" is agreeing to. */
export const OUR_TEXT_MAX = 800;

/**
 * How far back a reply is read, and how far back one becomes a task.
 *
 * Reading everything ever received on the first run would raise tasks for
 * replies answered by phone months ago, which a list cannot know about.
 */
export const REPLY_READ_DAYS = 14;

/**
 * The customer's own words, without the history their mail client quoted
 * under them.
 *
 * Every reply carries our message back underneath it, and our message is
 * exactly the text that says "please let us know if you'd like to go ahead".
 * Read whole, a one-word "Thanks" reads as a go-ahead. So the reply goes
 * through `stripQuotedReply`, the rule the Emails card already uses, and then
 * three things that rule does not need for a screen and Jev does: an
 * attribution line the client wrapped onto two lines, `>` lines left in the
 * middle, and a length a reader will actually get through. Falls back to
 * Gmail's snippet when the body is empty.
 */
export function replyText(
  body: string | null | undefined,
  snippet?: string | null,
  max: number = REPLY_TEXT_MAX,
): string {
  let text = (body ?? "").replace(/\r\n?/g, "\n");

  // "On Tue, Sep 16, 2026 at 10:02 AM Jonathon Yale <\nj@x.com> wrote:" —
  // one line to the sender, two to `stripQuotedReply`, which reads lines.
  const wrapped = text.search(
    /(^|\n)[ \t]*On\s[^\n]{0,200}\n[^\n]{0,200}\swrote:[ \t]*(\n|$)/i,
  );
  if (wrapped !== -1) text = text.slice(0, wrapped);

  text = stripQuotedReply(text)
    .split("\n")
    .filter((line) => !/^[ \t]*>/.test(line))
    .join("\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (text === "") text = (snippet ?? "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** One reply, as much of it as Jev reads. */
export interface ReplyToRead {
  /** The `email_messages` id. It is also the question's key. */
  id: string;
  subject: string | null;
  /** The customer's words, already through `replyText`. */
  text: string;
  /** Our last message in the conversation before this reply, if any. */
  ours: string | null;
  channel?: "email" | "text";
}

/** The body of one TypeSafe request: one Choice per reply. */
export function replyReadingRequest(replies: readonly ReplyToRead[]): {
  model: string;
  state: unknown;
  questions: Record<string, unknown>;
} {
  const state = {
    context:
      "Inbound customer emails and texts to a roofing company. Each message comes with its channel, optional subject, " +
      "the customer's own words with quoted email history removed, and the roofing company's last message before it, " +
      "which is what a short reply such as 'yes' or 'sounds good' is answering.",
    replies: replies.map((reply) => ({
      channel: reply.channel ?? "email",
      subject: reply.subject ?? "",
      reply: reply.text,
      our_last_message: reply.ours ?? "",
    })),
  };

  const criteria: Record<string, string> = {};
  for (const meaning of REPLY_MEANINGS) {
    criteria[meaning] = REPLY_MEANING_CRITERIA[meaning];
  }

  const questions: Record<string, unknown> = {};
  replies.forEach((reply, index) => {
    questions[reply.id] = {
      type: "choice",
      instructions:
        `What does the reply \`replies[${index}].reply\` need from the roofing company? ` +
        "Read it as an answer to `our_last_message` when it is short. " +
        "If it does more than one of these, choose the first that applies in this order: " +
        `${REPLY_MEANINGS.join(", ")}.`,
      criteria,
    };
  });

  return { model: REPLY_READING_MODEL, state, questions };
}

/** What a reading row stores for one reply. */
export interface ReplyReading {
  email_message_id: string;
  meaning: StoredReplyMeaning;
  probability: number;
}

/**
 * TypeSafe's answers, as rows. A reply with no answer, or an answer that is
 * not a meaning, is `unclear` at 0 — which the list shows as a reply owed.
 */
export function readReplyReading(
  answers: unknown,
  replies: readonly ReplyToRead[],
): ReplyReading[] {
  const all = (answers ?? {}) as Record<string, unknown>;
  const known = new Set<string>(REPLY_MEANINGS);
  return replies.map((reply) => {
    const answer = all[reply.id] as
      { choice?: unknown; probabilities?: unknown } | undefined;
    const choice = typeof answer?.choice === "string" ? answer.choice : null;
    if (!choice || !known.has(choice)) {
      return {
        email_message_id: reply.id,
        meaning: REPLY_UNCLEAR,
        probability: 0,
      };
    }
    const probabilities = (answer?.probabilities ?? {}) as Record<
      string,
      unknown
    >;
    const probability = Number(probabilities[choice]);
    return {
      email_message_id: reply.id,
      meaning: choice as ReplyMeaning,
      probability: Number.isFinite(probability)
        ? Math.min(1, Math.max(0, probability))
        : 0,
    };
  });
}

/**
 * How sure a reading is, as the list says it.
 *
 * The house rule: above 0.9 the list states it, from 0.5 it says "looks
 * like", and below that it says only that a reply is owed.
 */
export type ReplyCertainty = "stated" | "likely" | "unsure";

export const REPLY_STATE_ABOVE = 0.9;
export const REPLY_LIKELY_FROM = 0.5;

export function replyCertainty(probability: number): ReplyCertainty {
  if (probability > REPLY_STATE_ABOVE) return "stated";
  if (probability >= REPLY_LIKELY_FROM) return "likely";
  return "unsure";
}
